<template>
    <div
        :id="`fact-${fact.id}`"
        class="foxy-card !rounded-2xl p-6 !duration-300 flex flex-col justify-between"
        :class="{ 'notification-target': isNotificationTarget, 'h-full': presentationMode === 'full' }">
        <!-- User Info -->
        <div>
            <div class="flex items-center space-x-3 justify-between">
                <div class="flex items-center space-x-3">
                    <template v-if="fact.user && fact.user.username">
                        <a :href="profileUrl(fact.user.username)"
                           class="flex items-center space-x-3 group"
                           @click.stop>
                            <user-avatar
                                :src="fact.user.avatar_display || ''"
                                :name="fact.user.name"
                                size="sm"
                                class="bg-gradient-to-br from-blue-500 to-green-500 text-sm text-white transition-transform duration-200 group-hover:scale-110"
                            />
                            <div>
                                <div class="text-sm font-medium text-foxy-text group-hover:text-foxy-action transition-colors">
                                    {{ fact.user?.name || 'Користувач' }}
                                </div>
                                <div v-if="fact.user?.username"
                                    class="text-xs text-foxy-text-secondary group-hover:text-foxy-action transition-colors">
                                    {{ '@' + fact.user.username }}
                                </div>
                                <div class="text-xs text-foxy-text-secondary">
                                    {{ formatDate(fact.created_at) }}
                                </div>
                            </div>
                        </a>
                    </template>
                    <template v-else>
                        <user-avatar
                            :src="fact.user && fact.user.avatar_display ? fact.user.avatar_display : ''"
                            :name="fact.user ? fact.user.name : ''"
                            size="sm"
                            class="bg-gradient-to-br from-blue-500 to-green-500 text-sm text-white"
                        />
                        <div>
                            <div class="text-sm font-medium text-foxy-text">
                                {{ fact.user?.name || 'Користувач' }}
                            </div>
                            <div class="text-xs text-foxy-text-secondary">
                                {{ formatDate(fact.created_at) }}
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <a v-if="showBookContext && fact.book?.slug && fact.book?.title" :href="`/books/${fact.book.slug}`"
                class="mt-3 inline-flex text-sm font-semibold text-foxy-action hover:underline">
                {{ fact.book.title }}
            </a>
            <!-- Fact Text -->
            <i v-if="presentationMode === 'compact'" class="fas fa-lightbulb text-foxy-honey mt-3" aria-hidden="true"></i>
            <div v-if="!isEditing">
                <!-- Blocked Content -->
                <div v-if="fact.status === 'blocked'" class="my-3">
                    <div class="border-2 border-red-300 dark:border-red-700 rounded-lg bg-red-50/50 dark:bg-red-900/20 p-4">
                        <div class="relative overflow-hidden rounded-md mb-3">
                            <p class="text-lg text-foxy-text leading-relaxed font-medium break-words blur-sm filter"
                               style="word-break: break-word; overflow-wrap: break-word; hyphens: auto; -webkit-hyphens: auto; -ms-hyphens: auto;">
                                {{ fact.content }}
                            </p>
                        </div>
                        <div class="flex items-start gap-3 pt-2 border-t border-red-200 dark:border-red-800">
                            <div class="flex-shrink-0 mt-0.5">
                                <i class="fas fa-exclamation-triangle text-red-500 dark:text-red-400 text-lg"></i>
                            </div>
                            <div class="flex-1 min-w-0">
                                <p class="text-xs text-red-700 dark:text-red-300 font-medium mb-1">
                                    Контент заблоковано адміністрацією сайту
                                </p>
                                <p v-if="fact.moderation_reason" class="text-xs text-red-600 dark:text-red-400 mt-1">
                                    Причина: {{ fact.moderation_reason }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Normal Content -->
                <p v-else ref="content" class="whitespace-pre-line text-lg text-foxy-text leading-relaxed font-medium mt-3.5 sm:mt-4 mb-4 break-words"
                    :class="presentationMode === 'compact' && !isExpanded ? 'fact-clamped-compact' : ''"
                    style="word-break: break-word; overflow-wrap: break-word; hyphens: auto; -webkit-hyphens: auto; -ms-hyphens: auto;">{{ fact.content }}</p>
                <button v-if="fact.status !== 'blocked' && presentationMode === 'compact' && (isExpanded || isOverflowing)" type="button"
                    class="mb-3 mr-3 text-sm font-semibold text-foxy-action hover:underline" :aria-expanded="String(isExpanded)" @click="toggleExpanded">
                    {{ isExpanded ? 'Згорнути' : 'Розгорнути' }}
                </button>
                <a v-if="fact.source_url" :href="fact.source_url" target="_blank" rel="noopener noreferrer"
                    class="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-foxy-action hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/30">
                    Джерело
                    <i class="fas fa-external-link-alt text-xs" aria-hidden="true"></i>
                </a>
            </div>
            
            <!-- Edit Form -->
            <div v-else class="my-4">
                <textarea v-model="editContent" 
                    class="foxy-textarea !resize-none !p-3"
                    rows="3" placeholder="Введіть цікавий факт..."></textarea>
                <label :for="`fact-source-url-${fact.id}`" class="mb-1 mt-4 block text-sm font-semibold text-foxy-text">
                    Джерело <span class="font-normal text-foxy-text-secondary">(необов’язково)</span>
                </label>
                <input :id="`fact-source-url-${fact.id}`" v-model="editSourceUrl" type="url" maxlength="1024"
                    class="foxy-input w-full" placeholder="https://example.com/material">
                <p v-if="editError" class="mt-2 text-sm text-foxy-error" role="alert">{{ editError }}</p>
                <div class="flex space-x-2 mt-3">
                    <button @click="saveEdit" :disabled="isSaving"
                        class="foxy-btn-primary !px-4 !py-2 !text-sm">
                        {{ isSaving ? 'Збереження...' : 'Зберегти' }}
                    </button>
                    <button @click="cancelEdit" :disabled="isSaving"
                        class="foxy-btn-secondary !px-4 !py-2 !text-sm">
                        Скасувати
                    </button>
                </div>
            </div>
        </div>
        <div>
            <!-- Actions -->
            <div class="flex items-center space-x-4">
                <!-- Like Button -->
                <button @click="toggleLike"
                    class="flex items-center space-x-1 text-foxy-text-secondary hover:text-red-500 dark:hover:text-red-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/30 rounded-sm">
                    <i :class="['fas fa-heart', isLiked ? 'text-red-500 dark:text-red-400' : '']"></i>
                    <span class="text-sm">{{ likesCount }}</span>
                </button>

                <!-- Share Button -->
                <button @click="shareFact"
                    class="flex items-center space-x-1 text-foxy-text-secondary hover:text-foxy-action transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/30 rounded-sm">
                    <i class="fas fa-share-alt"></i>
                </button>

                <!-- Edit Button (for owner) -->
                <button v-if="canEdit && !isEditing" @click="startEdit"
                    class="text-foxy-text-secondary hover:text-foxy-action transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/30 rounded-sm">
                    <i class="fas fa-edit text-sm"></i>
                </button>

                <!-- Delete Button (for owner) -->
                <button v-if="canDelete" @click="deleteFact"
                    class="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors">
                    <i class="fas fa-trash text-sm"></i>
                </button>

                <!-- Report Button -->
                <report-button 
                    :reportable-type="'App\\Models\\Fact'"
                    :reportable-id="fact.id"
                    :content-preview="getContentPreview()"
                    :content-url="getContentUrl()">
                </report-button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { shareContent } from '../../utils/shareHelper';

export default {
    name: 'FactCard',
    props: {
        fact: {
            type: Object,
            required: true
        },
        bookSlug: {
            type: String,
            required: true
        },
        currentUserId: {
            type: Number,
            default: null
        },
        isNotificationTarget: {
            type: Boolean,
            default: false
        },
        showBookContext: { type: Boolean, default: false },
        presentationMode: {
            type: String,
            default: 'full',
            validator: value => ['full', 'compact'].includes(value)
        }
    },
    data() {
        return {
            isLiked: this.fact.is_liked_by_current_user || false,
            likesCount: this.fact.likes_count || 0,
            isExpanded: false,
            isOverflowing: false,
            isEditing: false,
            editContent: '',
            editSourceUrl: '',
            isSaving: false,
            editError: '',
        };
    },
    computed: {
        canEdit() {
            return this.currentUserId && this.fact.user_id === this.currentUserId;
        },
        canDelete() {
            return this.currentUserId && this.fact.user_id === this.currentUserId;
        }
    },
    mounted() {
        this.$nextTick(this.checkOverflow);
        window.addEventListener('resize', this.checkOverflow);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.checkOverflow);
    },
    watch: {
        'fact.content'() { this.isExpanded = false; this.$nextTick(this.checkOverflow); },
        isEditing() { this.$nextTick(this.checkOverflow); }
    },
    methods: {
        checkOverflow() {
            if (this.presentationMode !== 'compact' || this.isExpanded) return;
            this.$nextTick(() => {
                const element = this.$refs.content;
                this.isOverflowing = Boolean(element && element.scrollHeight > element.clientHeight + 1);
            });
        },
        toggleExpanded() {
            this.isExpanded = !this.isExpanded;
            if (!this.isExpanded) this.$nextTick(this.checkOverflow);
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            const now = new Date();
            const diff = Math.floor((now - date) / 1000);

            if (diff < 60) return 'щойно';
            if (diff < 3600) return `${Math.floor(diff / 60)} хв тому`;
            if (diff < 86400) return `${Math.floor(diff / 3600)} год тому`;
            if (diff < 604800) return `${Math.floor(diff / 86400)} дн тому`;

            return date.toLocaleDateString('uk-UA', {
                day: 'numeric',
                month: 'short',
                year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
            });
        },
        async toggleLike() {
            if (!this.currentUserId) {
                this.$emit('show-notification', 'Будь ласка, увійдіть, щоб поставити лайк.', 'error');
                return;
            }
            try {
                const response = await axios.post(`/books/${this.bookSlug}/facts/${this.fact.id}/like`);
                if (response.data.success) {
                    this.isLiked = response.data.is_liked;
                    this.likesCount = response.data.likes_count;
                    this.$emit('like-toggled', { factId: this.fact.id, isLiked: this.isLiked, likesCount: this.likesCount });
                }
            } catch (error) {
                console.error('Error toggling like:', error);
                this.$emit('show-notification', 'Помилка при зміні лайка.', 'error');
            }
        },
        async shareFact() {
            const text = `Цікавий факт про книгу: ${this.fact.content}`;
            const url = `${window.location.origin}/books/${this.bookSlug}#fact-${this.fact.id}`;
            await shareContent({
                title: 'Цікавий факт',
                text: text,
                url: url
            });
        },
        startEdit() {
            this.isEditing = true;
            this.editContent = this.fact.content;
            this.editSourceUrl = String(this.fact.source_url || '');
            this.editError = '';
        },
        cancelEdit() {
            this.isEditing = false;
            this.editContent = '';
            this.editSourceUrl = '';
            this.editError = '';
        },
        async saveEdit() {
            if (!this.editContent.trim()) {
                this.editError = 'Будь ласка, введіть текст факту.';
                return;
            }

            this.editError = '';
            this.isSaving = true;
            try {
                const response = await axios.put(`/books/${this.bookSlug}/facts/${this.fact.id}`, {
                    content: this.editContent.trim(),
                    source_url: this.editSourceUrl.trim() || null,
                });

                if (response.data.success) {
                    this.$emit('show-notification', 'Факт оновлено!', 'success');
                    this.$emit('fact-updated', response.data.fact);
                    this.isEditing = false;
                    this.editContent = '';
                    this.editSourceUrl = '';
                    this.editError = '';
                } else {
                    this.$emit('show-notification', response.data.message || 'Помилка при оновленні факту.', 'error');
                }
            } catch (error) {
                console.error('Error updating fact:', error);
                this.editError = error.response?.data?.errors?.source_url?.[0]
                    || error.response?.data?.errors?.content?.[0]
                    || error.response?.data?.message
                    || 'Помилка при оновленні факту.';
                this.$emit('show-notification', this.editError, 'error');
            } finally {
                this.isSaving = false;
            }
        },
        async deleteFact() {
            const confirmed = await confirm('Ви впевнені, що хочете видалити цей факт?', 'Підтвердження', 'warning');
            if (!confirmed) return;
            try {
                const response = await axios.delete(`/books/${this.bookSlug}/facts/${this.fact.id}`);
                if (response.data.success) {
                    this.$emit('show-notification', 'Факт видалено!', 'success');
                    this.$emit('fact-deleted', this.fact.id);
                } else {
                    this.$emit('show-notification', response.data.message || 'Помилка при видаленні факту.', 'error');
                }
            } catch (error) {
                console.error('Error deleting fact:', error);
                this.$emit('show-notification', error.response?.data?.message || 'Помилка при видаленні факту.', 'error');
            }
        },

        getContentPreview() {
            // Возвращаем превью контента для жалобы
            if (this.fact.content) {
                const text = this.fact.content;
                return text.substring(0, 100) + (text.length > 100 ? '...' : '');
            }
            return 'Цікавий факт про книгу';
        },

        getContentUrl() {
            // Возвращаем URL контента
            return `/books/${this.bookSlug}#fact-${this.fact.id}`;
        },
        profileUrl(username) {
            return username ? `/users/${username}` : '#';
        }
    }
};
</script>

<style scoped>
.fact-clamped-compact {
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Fact card styles inherited from parent */

.notification-target {
    border-color: var(--color-action, #f97316);
    background-color: color-mix(in srgb, var(--color-action, #f97316) 8%, transparent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-action, #f97316) 24%, transparent);
}

@media (prefers-reduced-motion: reduce) {
    .notification-target {
        transition: none !important;
    }
}
</style>
